import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectComponent } from '@ng-select/ng-select';
import {
  DatePickerComponent,
  ModalBodyComponent,
  ModalComponent,
  ModalFooterAction,
  ModalFooterComponent,
  ModalHeaderComponent,
} from '@groundworks/ngx-groundworks-ui';
import {
  ApiClient,
  CreatePermitRequest,
  EngineeringDocStatus,
  EngineeringIntakeProvidedByCfi,
  ExternalPermitStatus,
  FinalInspectionStatus,
  FinalInspectionType,
  MidInspectionStatus,
  MidInspectionType,
  PermitAsBuildRequiredStatus,
  PermitHeaderPatch,
  PermitPhase,
  PermitPhase2Patch,
  PermitPhase3Patch,
  PermitPhase4Patch,
  PermitPhase5Patch,
  PermitPhaseCompletionStatus,
  PermitPhaseStatusPatch,
  PermitReminders,
  PermitRemindersPatch,
  PermitInfo,
  PermitStatus,
  PreConstructionMeetingStatus,
  PreparationItemStatus,
  SignedStatementStatus,
  SubcontractedPermitStatus,
  ThirdPartyInspectionsStatus,
  UpdatePermitRequest,
  UserInfo,
} from '../../sdk/sdk';
import { UtcToLocalPipe } from '../../pipes/utc-to-local.pipe';
import { ModalService } from '../../services/modal.service';
import { formatUtcIsoDate } from '../../utils/iso-date';
import { roundMoney } from '../../utils/money';
import { MultiPermitTypeSelectorComponent } from '../multi-permit-type-selector/multi-permit-type-selector.component';
import { ENGINEERING_DOC_STATUS_LABELS } from '../../constants/engineering-doc-status.constants';
import { PERMIT_STATUS_LABELS } from '../../constants/permit-status.constants';

export interface PermitEditResult {
  action: 'created' | 'updated' | 'deleted';
  id: string;
}

@Component({
  selector: 'app-permit-modal',
  imports: [
    DatePipe,
    FormsModule,
    DatePickerComponent,
    ModalComponent,
    ModalHeaderComponent,
    ModalBodyComponent,
    ModalFooterComponent,
    MultiPermitTypeSelectorComponent,
    NgSelectComponent,
    UtcToLocalPipe,
  ],
  templateUrl: './permit-modal.component.html',
  styleUrl: './permit-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PermitModalComponent {
  private readonly activeModal = inject(NgbActiveModal);
  private readonly apiClient = inject(ApiClient);
  private readonly destroyRef = inject(DestroyRef);
  private readonly modalService = inject(ModalService);

  readonly Completed = PermitPhaseCompletionStatus.Completed;
  readonly NotNeeded = PermitPhaseCompletionStatus.NotNeeded;
  readonly ProgramDetails = PermitPhase.ProgramDetails;
  readonly Preparation = PermitPhase.Preparation;
  readonly Engineering = PermitPhase.Engineering;
  readonly Building = PermitPhase.Building;
  readonly OpenPermit = PermitPhase.OpenPermit;

  readonly cfiIntakeOptions = [
    { value: EngineeringIntakeProvidedByCfi.Yes, label: 'Yes' },
    { value: EngineeringIntakeProvidedByCfi.No, label: 'No' },
  ] as const;

  readonly preparationItemStatusOptions = [
    { value: PreparationItemStatus.NotApplicable, label: 'N/A' },
    { value: PreparationItemStatus.RequiredOrdered, label: 'Required - Ordered' },
    { value: PreparationItemStatus.RequiredNotOrdered, label: 'Required / Not yet Ordered' },
    { value: PreparationItemStatus.CompleteDocUploaded, label: 'Complete - Doc uploaded' },
  ] as const;

  readonly subcontractedPermitStatusOptions = [
    { value: SubcontractedPermitStatus.NotApplicable, label: 'N/A' },
    { value: SubcontractedPermitStatus.RequiredSubmitted, label: 'Required - Submitted' },
    { value: SubcontractedPermitStatus.RequiredNotYetSubmitted, label: 'Required - Not Yet Submitted' },
    { value: SubcontractedPermitStatus.CompletePermitReceived, label: 'Complete - Permit Received' },
    { value: SubcontractedPermitStatus.SubcontractorResponsibility, label: 'Subcontractor Responsibility' },
  ] as const;

  readonly engineeringDocStatusOptions = Object.values(EngineeringDocStatus)
    .map(value => ({ value, label: ENGINEERING_DOC_STATUS_LABELS[value] }));

  readonly thirdPartyInspectionsOptions = [
    { value: ThirdPartyInspectionsStatus.NotApplicable, label: 'N/A' },
    { value: ThirdPartyInspectionsStatus.CountyAllowsScheduled, label: 'County Allows - Scheduled' },
    { value: ThirdPartyInspectionsStatus.CountyAllowsNotScheduled, label: 'County Allows - Not Yet Scheduled' },
  ] as const;

  readonly signedStatementOptions = [
    { value: SignedStatementStatus.NotApplicable, label: 'N/A' },
    { value: SignedStatementStatus.RequiredSent, label: 'Required - Sent' },
    { value: SignedStatementStatus.RequiredNotYetSent, label: 'Required - Not Yet Sent' },
    { value: SignedStatementStatus.CompleteDocUploaded, label: 'Complete - Doc uploaded' },
  ] as const;

  readonly preConstructionMeetingOptions = [
    { value: PreConstructionMeetingStatus.NotApplicable, label: 'N/A' },
    { value: PreConstructionMeetingStatus.InHousePmGmPe, label: 'In house - PM/GM/PE' },
    { value: PreConstructionMeetingStatus.CountyInspector, label: 'County Inspector' },
    { value: PreConstructionMeetingStatus.Completed, label: 'Completed' },
  ] as const;

  readonly buildingPermitStatusOptions = Object.values(PermitStatus)
    .map(value => ({ value, label: PERMIT_STATUS_LABELS[value] }));

  readonly midInspectionTypeOptions = [
    { value: MidInspectionType.NotApplicable, label: 'N/A' },
    { value: MidInspectionType.ThirdParty, label: '3rd Party' },
    { value: MidInspectionType.Eor, label: 'EOR' },
    { value: MidInspectionType.CountyInspector, label: 'County Inspector' },
    { value: MidInspectionType.ContinuousInspection, label: 'Continuous Inspection' },
  ] as const;

  readonly midInspectionStatusOptions = [
    { value: MidInspectionStatus.NotApplicable, label: 'N/A' },
    { value: MidInspectionStatus.RequiredScheduled, label: 'Required - Scheduled' },
    { value: MidInspectionStatus.CompleteAwaitingDoc, label: 'Complete - Awaiting Doc' },
    { value: MidInspectionStatus.CompleteDocUploaded, label: 'Complete - Doc Uploaded' },
    { value: MidInspectionStatus.NeedToReschedule, label: 'Need to Reschedule' },
  ] as const;

  readonly externalPermitStatusOptions = [
    { value: ExternalPermitStatus.NotApplicable, label: 'N/A' },
    { value: ExternalPermitStatus.SubmittedAwaitingDoc, label: 'Submitted - Awaiting Doc' },
    { value: ExternalPermitStatus.CompleteDocUploaded, label: 'Complete - Doc Uploaded' },
    { value: ExternalPermitStatus.SubcontractorResponsibility, label: 'Subcontractor Responsibility' },
  ] as const;

  readonly permitAsBuildRequiredOptions = [
    { value: PermitAsBuildRequiredStatus.NotApplicable, label: 'N/A' },
    { value: PermitAsBuildRequiredStatus.RequiredNotComplete, label: 'Required - Not Complete' },
    { value: PermitAsBuildRequiredStatus.CompleteDocUploaded, label: 'Complete - Doc Uploaded' },
  ] as const;

  readonly finalInspectionTypeOptions = [
    { value: FinalInspectionType.NotApplicable, label: 'N/A' },
    { value: FinalInspectionType.ThirdParty, label: '3rd Party' },
    { value: FinalInspectionType.EorLetter, label: 'EOR - Letter' },
    { value: FinalInspectionType.CountyInspector, label: 'County Inspector' },
  ] as const;

  readonly finalInspectionStatusOptions = [
    { value: FinalInspectionStatus.ClosedDocUploaded, label: 'Closed - Doc Uploaded' },
    { value: FinalInspectionStatus.SubmittedAwaitingResponse, label: 'Submitted - Awaiting Response' },
    { value: FinalInspectionStatus.Failed, label: 'Failed' },
    { value: FinalInspectionStatus.NeedToReschedule, label: 'Need to Reschedule' },
    { value: FinalInspectionStatus.NaPermitClosed, label: 'N/A - Permit Closed' },
  ] as const;

  readonly projectId = signal('');
  readonly permitId = signal('');
  readonly branchId = signal('');

  readonly permit = signal<PermitInfo | undefined>(undefined);
  readonly isSaving = signal(false);
  readonly isDeleting = signal(false);
  readonly isLoading = signal(false);
  readonly saveError = signal<string | undefined>(undefined);

  /**
   * Which phase the modal is currently displaying. Driven by Previous/Next nav.
   * Unified save: navigation freely shifts the visible phase; one Save/Update at the
   * bottom commits *all* engaged-phase data atomically. Closing the modal discards
   * unsaved edits.
   */
  readonly currentPhase = signal<PermitPhase>(PermitPhase.ProgramDetails);

  /**
   * Phases the user has engaged with during this modal session — drives which phase
   * patches the save payload carries (DATA-STRUCTURES §5 lazy phase-status creation).
   * Phase 1 (ProgramDetails) is always engaged because the header lives there and the
   * aggregate seeds Phase 1 on Permit.Create.
   */
  readonly visitedPhases = signal<ReadonlySet<PermitPhase>>(new Set([PermitPhase.ProgramDetails]));

  private markVisited(phase: PermitPhase): void {
    this.visitedPhases.update(current => {
      if (current.has(phase)) return current;
      const next = new Set(current);
      next.add(phase);
      return next;
    });
  }

  readonly isEditMode = computed(() => !!this.permit());

  readonly modalTitle = computed(() => {
    const prefix = this.isEditMode() ? 'Edit Permit' : 'Add Permit';
    const phase = this.currentPhase();
    const phaseLabel =
      phase === PermitPhase.OpenPermit ? 'Open Permit'
      : phase === PermitPhase.Building ? 'Building'
      : phase === PermitPhase.Engineering ? 'Engineering'
      : phase === PermitPhase.Preparation ? 'Preparation'
      : 'Program Details';
    return `${prefix}: ${phaseLabel}`;
  });

  // Phase nav: Previous disabled on the first phase; Next disabled on the last phase.
  readonly canGoPrevious = computed(() => this.currentPhase() !== PermitPhase.ProgramDetails);
  readonly canGoNext = computed(() => this.currentPhase() !== PermitPhase.OpenPermit);

  goToPreviousPhase(): void {
    const target =
      this.currentPhase() === PermitPhase.Preparation ? PermitPhase.ProgramDetails
      : this.currentPhase() === PermitPhase.Engineering ? PermitPhase.Preparation
      : this.currentPhase() === PermitPhase.Building ? PermitPhase.Engineering
      : this.currentPhase() === PermitPhase.OpenPermit ? PermitPhase.Building
      : undefined;
    if (target === undefined) return;
    this.markVisited(target);
    this.currentPhase.set(target);
  }

  goToNextPhase(): void {
    const target =
      this.currentPhase() === PermitPhase.ProgramDetails ? PermitPhase.Preparation
      : this.currentPhase() === PermitPhase.Preparation ? PermitPhase.Engineering
      : this.currentPhase() === PermitPhase.Engineering ? PermitPhase.Building
      : this.currentPhase() === PermitPhase.Building ? PermitPhase.OpenPermit
      : undefined;
    if (target === undefined) return;
    this.markVisited(target);
    this.currentPhase.set(target);
  }

  /** Phase 1's persisted PermitPhaseStatus — captured at load. */
  readonly persistedPhase1 = computed(() =>
    (this.permit()?.phaseStatuses ?? []).find(s => asPhase(s.phase) === PermitPhase.ProgramDetails),
  );

  /** Phase 2's persisted PermitPhaseStatus — captured at load. */
  readonly persistedPhase2 = computed(() =>
    (this.permit()?.phaseStatuses ?? []).find(s => asPhase(s.phase) === PermitPhase.Preparation),
  );

  /** Phase 3's persisted PermitPhaseStatus — captured at load. */
  readonly persistedPhase3 = computed(() =>
    (this.permit()?.phaseStatuses ?? []).find(s => asPhase(s.phase) === PermitPhase.Engineering),
  );

  /** Phase 4's persisted PermitPhaseStatus — captured at load. */
  readonly persistedPhase4 = computed(() =>
    (this.permit()?.phaseStatuses ?? []).find(s => asPhase(s.phase) === PermitPhase.Building),
  );

  /** Phase 5's persisted PermitPhaseStatus — captured at load. */
  readonly persistedPhase5 = computed(() =>
    (this.permit()?.phaseStatuses ?? []).find(s => asPhase(s.phase) === PermitPhase.OpenPermit),
  );

  /** Current phase's persisted row — driver for lock + stamp display. */
  readonly persistedCurrentPhase = computed(() => {
    const phase = this.currentPhase();
    if (phase === PermitPhase.OpenPermit) return this.persistedPhase5();
    if (phase === PermitPhase.Building) return this.persistedPhase4();
    if (phase === PermitPhase.Engineering) return this.persistedPhase3();
    if (phase === PermitPhase.Preparation) return this.persistedPhase2();
    return this.persistedPhase1();
  });

  /**
   * Once a phase is persisted as Completed or NotNeeded the radio collapses into a
   * read-only stamp. The aggregate's idempotency guard preserves CompletedAtUtc/ById
   * when re-saving with the same terminal status, so it's safe to keep sending the
   * persisted Status on subsequent updates.
   */
  readonly isPhaseLocked = computed(() => {
    const status = asStatus(this.persistedCurrentPhase()?.status);
    return status === PermitPhaseCompletionStatus.Completed
      || status === PermitPhaseCompletionStatus.NotNeeded;
  });

  readonly phaseCompletionLabel = computed(() => {
    const status = asStatus(this.persistedCurrentPhase()?.status);
    if (status === PermitPhaseCompletionStatus.Completed) return 'Completed';
    if (status === PermitPhaseCompletionStatus.NotNeeded) return 'Not Needed';
    return undefined;
  });

  /**
   * MOD-3775 — the user can only mark the current phase Completed/NotNeeded once every
   * earlier phase is itself resolved. The server is the source of truth: PermitInfo
   * carries the list of phases that are currently resolvable. While the permit is still
   * loading we default to true so the controls aren't gated on a transient empty state.
   */
  readonly canResolveCurrentPhase = computed(() => {
    const info = this.permit();
    if (!info) return true;
    return (info.resolvablePhases ?? []).some(p => asPhase(p) === this.currentPhase());
  });

  readonly phaseResolveDisabledReason = computed(() => {
    if (this.canResolveCurrentPhase()) return null;
    return 'Complete all prior phases before marking this phase Completed or Not Needed.';
  });

  // Header (Phase 1 body) fields
  readonly permitNumber = signal('');
  readonly permitName = signal('');
  readonly countyTownship = signal('');
  readonly permitTypeIds = signal<string[]>([]);

  // Right-rail (PermitPhaseStatus row for ProgramDetails)
  readonly nextActionDate = signal<string | undefined>(undefined);
  readonly notes = signal('');
  readonly phaseStatus = signal<PermitPhaseCompletionStatus | undefined>(undefined);

  // Hard cap matches the Touchpoints backend limit (TouchpointHandler.MaxContentLength = 2000).
  readonly maxNotesLength = 2000;
  readonly notesCharacterCount = computed(() => this.notes().length);

  readonly showNextActionDatePicker = signal(false);

  readonly nextActionDateAsDate = computed(() => {
    const iso = this.nextActionDate();
    return iso ? new Date(iso + 'T00:00:00') : undefined;
  });

  // Per-dropdown TODO reminders. The checkbox next to a dropdown means "I want to come
  // back and fill this in later"; it does not gate the dropdown's enabled state (N/A is
  // a real choice on every status enum). Persisted as a single JSON column on Permits.
  // `loadedReminders` is the snapshot at load time so we can diff at save and only ship
  // the flags the user actually toggled this session.
  readonly reminders = signal<PermitReminders>(new PermitReminders());
  private loadedReminders: PermitReminders = new PermitReminders();

  // ───────────────────────────── Phase 2 — Preparation ─────────────────────────────
  // Header fields (left + middle columns in the figma).
  readonly engineeringIntakeProvidedByCfi = signal<EngineeringIntakeProvidedByCfi | undefined>(undefined);
  readonly cfiRemainingNeeds = signal('');
  readonly assignedAdminCad = signal('');

  readonly soilReport = signal<PreparationItemStatus | undefined>(undefined);
  readonly soilReportExpectedReturnDate = signal<string | undefined>(undefined);
  readonly showSoilReportExpectedDatePicker = signal(false);

  readonly platSurveyIlc = signal<PreparationItemStatus | undefined>(undefined);

  readonly neighborNotification = signal<PreparationItemStatus | undefined>(undefined);

  readonly testPitTestPier = signal<PreparationItemStatus | undefined>(undefined);
  readonly testPitDate = signal<string | undefined>(undefined);
  readonly showTestPitDatePicker = signal(false);

  readonly electricalPermitNeeded = signal<SubcontractedPermitStatus | undefined>(undefined);
  readonly electricalPermitNumber = signal('');

  readonly plumbingPermitNeeded = signal<SubcontractedPermitStatus | undefined>(undefined);
  readonly plumbingPermitNumber = signal('');

  // Phase 2 right-rail — separate from Phase 1's so phase navigation doesn't blow them away.
  readonly phase2NextActionDate = signal<string | undefined>(undefined);
  readonly phase2Notes = signal('');
  readonly phase2Status = signal<PermitPhaseCompletionStatus | undefined>(undefined);
  readonly showPhase2NextActionDatePicker = signal(false);

  readonly phase2NotesCharacterCount = computed(() => this.phase2Notes().length);
  readonly phase2NextActionDateAsDate = computed(() => {
    const iso = this.phase2NextActionDate();
    return iso ? new Date(iso + 'T00:00:00') : undefined;
  });
  readonly testPitDateAsDate = computed(() => {
    const iso = this.testPitDate();
    return iso ? new Date(iso + 'T00:00:00') : undefined;
  });
  readonly soilReportExpectedReturnDateAsDate = computed(() => {
    const iso = this.soilReportExpectedReturnDate();
    return iso ? new Date(iso + 'T00:00:00') : undefined;
  });

  // ───────────────────────────── Phase 3 — Engineering ─────────────────────────────
  // Engineer of Record dropdown is populated from /users/engineers-of-record (MOD-3653).
  readonly engineerOfRecordUsers = signal<UserInfo[]>([]);
  readonly engineerOfRecordUserId = signal<string | undefined>(undefined);
  readonly engineerOfRecordLoadError = signal<string | null>(null);

  readonly expectedEngineerReturnDate = signal<string | undefined>(undefined);
  readonly showExpectedEngineerReturnDatePicker = signal(false);

  readonly engineeringDocStatus = signal<EngineeringDocStatus | undefined>(undefined);

  // Soil Report / Plat-Survey-ILC / Test Pit/Pier are shared with Phase 2 (single DB column);
  // reuse the existing Phase 2 signals (soilReport*, platSurveyIlc*, testPitTestPier*).

  readonly thirdPartyInspections = signal<ThirdPartyInspectionsStatus | undefined>(undefined);
  readonly thirdPartyInspector = signal('');

  readonly signedStatementOfSpecialInspection = signal<SignedStatementStatus | undefined>(undefined);

  readonly preConstructionMeeting = signal<PreConstructionMeetingStatus | undefined>(undefined);
  readonly preConstructionMeetingDate = signal<string | undefined>(undefined);
  readonly showPreConstructionMeetingDatePicker = signal(false);

  // Phase 3 right-rail — separate signals so navigation doesn't blow away unsaved edits.
  readonly phase3NextActionDate = signal<string | undefined>(undefined);
  readonly phase3Notes = signal('');
  readonly phase3Status = signal<PermitPhaseCompletionStatus | undefined>(undefined);
  readonly showPhase3NextActionDatePicker = signal(false);

  readonly phase3NotesCharacterCount = computed(() => this.phase3Notes().length);
  readonly phase3NextActionDateAsDate = computed(() => {
    const iso = this.phase3NextActionDate();
    return iso ? new Date(iso + 'T00:00:00') : undefined;
  });
  readonly expectedEngineerReturnDateAsDate = computed(() => {
    const iso = this.expectedEngineerReturnDate();
    return iso ? new Date(iso + 'T00:00:00') : undefined;
  });
  readonly preConstructionMeetingDateAsDate = computed(() => {
    const iso = this.preConstructionMeetingDate();
    return iso ? new Date(iso + 'T00:00:00') : undefined;
  });

  // ───────────────────────────── Phase 4 — Building ─────────────────────────────
  readonly permitAppSentDate = signal<string | undefined>(undefined);
  readonly showPermitAppSentDatePicker = signal(false);

  readonly buildingExpectedReturnDate = signal<string | undefined>(undefined);
  readonly showBuildingExpectedReturnDatePicker = signal(false);

  readonly buildingPermitStatus = signal<PermitStatus | undefined>(undefined);

  // costOfPermits / costOfThirdPartyDocsServices: backend is decimal?, UI uses number | undefined.
  // The *Display string signals back the $-prefixed text input; they're parsed into the
  // numeric signal on blur and reformatted to two decimals.
  readonly costOfPermits = signal<number | undefined>(undefined);
  readonly costOfPermitsDisplay = signal<string>('');
  readonly costOfThirdPartyDocsServices = signal<number | undefined>(undefined);
  readonly costOfThirdPartyDocsServicesDisplay = signal<string>('');

  readonly permitExpirationDate = signal<string | undefined>(undefined);
  readonly showPermitExpirationDatePicker = signal(false);

  // Phase 4 right-rail — separate from other phases so navigation preserves edits.
  readonly phase4NextActionDate = signal<string | undefined>(undefined);
  readonly phase4Notes = signal('');
  readonly phase4Status = signal<PermitPhaseCompletionStatus | undefined>(undefined);
  readonly showPhase4NextActionDatePicker = signal(false);

  readonly phase4NotesCharacterCount = computed(() => this.phase4Notes().length);
  readonly phase4NextActionDateAsDate = computed(() => {
    const iso = this.phase4NextActionDate();
    return iso ? new Date(iso + 'T00:00:00') : undefined;
  });
  readonly permitAppSentDateAsDate = computed(() => {
    const iso = this.permitAppSentDate();
    return iso ? new Date(iso + 'T00:00:00') : undefined;
  });
  readonly buildingExpectedReturnDateAsDate = computed(() => {
    const iso = this.buildingExpectedReturnDate();
    return iso ? new Date(iso + 'T00:00:00') : undefined;
  });
  readonly permitExpirationDateAsDate = computed(() => {
    const iso = this.permitExpirationDate();
    return iso ? new Date(iso + 'T00:00:00') : undefined;
  });

  // ───────────────────────────── Phase 5 — Open Permit ─────────────────────────────
  // Mid Inspection Type, Permit As-Build Required, and Final Inspection Type each have
  // a reminder checkbox driven by `reminders`. All dropdowns are always enabled — N/A
  // is a first-class enum choice on every status/type field, not a UI null sentinel.
  readonly midInspectionType = signal<MidInspectionType | undefined>(undefined);
  readonly midInspectionDate = signal<string | undefined>(undefined);
  readonly showMidInspectionDatePicker = signal(false);
  readonly midInspectionInspector = signal('');
  readonly midInspectionStatus = signal<MidInspectionStatus | undefined>(undefined);

  readonly electricalPermitStatus = signal<ExternalPermitStatus | undefined>(undefined);
  readonly plumbingPermitStatus = signal<ExternalPermitStatus | undefined>(undefined);

  readonly permitAsBuildRequired = signal<PermitAsBuildRequiredStatus | undefined>(undefined);

  readonly finalInspectionType = signal<FinalInspectionType | undefined>(undefined);
  readonly finalInspectionDate = signal<string | undefined>(undefined);
  readonly showFinalInspectionDatePicker = signal(false);
  readonly finalInspectionInspector = signal('');
  readonly finalInspectionStatus = signal<FinalInspectionStatus | undefined>(undefined);
  readonly inspectionFailureReason = signal('');

  // Phase 5 right-rail
  readonly phase5NextActionDate = signal<string | undefined>(undefined);
  readonly phase5Notes = signal('');
  readonly phase5Status = signal<PermitPhaseCompletionStatus | undefined>(undefined);
  readonly showPhase5NextActionDatePicker = signal(false);

  readonly phase5NotesCharacterCount = computed(() => this.phase5Notes().length);
  readonly phase5NextActionDateAsDate = computed(() => {
    const iso = this.phase5NextActionDate();
    return iso ? new Date(iso + 'T00:00:00') : undefined;
  });
  readonly midInspectionDateAsDate = computed(() => {
    const iso = this.midInspectionDate();
    return iso ? new Date(iso + 'T00:00:00') : undefined;
  });
  readonly finalInspectionDateAsDate = computed(() => {
    const iso = this.finalInspectionDate();
    return iso ? new Date(iso + 'T00:00:00') : undefined;
  });

  readonly canSave = computed(() =>
    !this.isSaving() && !this.isDeleting() && !this.isLoading());

  readonly footerActions = computed<ModalFooterAction[]>(() => {
    // Unified save: one footer drives the whole modal regardless
    // of the visible phase. Create → [Save]; Edit → [Delete, Update]. The handler commits
    // every engaged phase atomically.
    const saving = this.isSaving();
    const deleting = this.isDeleting();

    if (!this.isEditMode()) {
      return [{
        label: 'Save',
        type: 'primary',
        action: () => this.save(),
        loading: saving,
        disabled: !this.canSave(),
      }];
    }

    return [
      {
        label: 'Delete',
        type: 'outline-danger',
        action: () => this.delete(),
        loading: deleting,
        disabled: saving || deleting,
      },
      {
        label: 'Update',
        type: 'primary',
        action: () => this.save(),
        loading: saving,
        disabled: !this.canSave(),
      },
    ];
  });

  loadPermit(): void {
    if (!this.projectId() || !this.permitId()) return;
    this.isLoading.set(true);
    this.apiClient.getPermitById(this.projectId(), this.permitId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          this.permit.set(response);
          this.populateFromResponse(response);
          this.isLoading.set(false);
        },
        error: () => {
          this.isLoading.set(false);
          this.saveError.set('Failed to load permit.');
        },
      });
  }

  onNotesInput(event: Event): void {
    const value = (event.target as HTMLTextAreaElement).value;
    if (value.length <= this.maxNotesLength) this.notes.set(value);
  }

  togglePhaseStatusRadio(target: PermitPhaseCompletionStatus): void {
    // Clicking the active radio clears the selection (= InProgress).
    this.phaseStatus.update(current => current === target ? undefined : target);
  }

  toggleNextActionDatePicker(): void {
    this.showNextActionDatePicker.update(v => !v);
  }

  handleNextActionDateSelected(date: Date | null): void {
    this.nextActionDate.set(date ? toIsoDate(date) : undefined);
    this.showNextActionDatePicker.set(false);
  }

  // ───────────────────────────── Phase 2 input handlers ─────────────────────────────

  onPhase2NotesInput(event: Event): void {
    const value = (event.target as HTMLTextAreaElement).value;
    if (value.length <= this.maxNotesLength) this.phase2Notes.set(value);
  }

  togglePhase2StatusRadio(target: PermitPhaseCompletionStatus): void {
    this.phase2Status.update(current => current === target ? undefined : target);
  }

  togglePhase2NextActionDatePicker(): void {
    this.showPhase2NextActionDatePicker.update(v => !v);
  }

  handlePhase2NextActionDateSelected(date: Date | null): void {
    this.phase2NextActionDate.set(date ? toIsoDate(date) : undefined);
    this.showPhase2NextActionDatePicker.set(false);
  }

  toggleTestPitDatePicker(): void {
    this.showTestPitDatePicker.update(v => !v);
  }

  handleTestPitDateSelected(date: Date | null): void {
    this.testPitDate.set(date ? toIsoDate(date) : undefined);
    this.showTestPitDatePicker.set(false);
  }

  toggleSoilReportExpectedDatePicker(): void {
    this.showSoilReportExpectedDatePicker.update(v => !v);
  }

  handleSoilReportExpectedDateSelected(date: Date | null): void {
    this.soilReportExpectedReturnDate.set(date ? toIsoDate(date) : undefined);
    this.showSoilReportExpectedDatePicker.set(false);
  }

  /**
   * Sets a TODO-reminder flag for one of the dropdowns. The checkbox is purely a memory
   * aid — it does not gate the dropdown's enabled state. Reminders are user-controlled
   * only; nothing else in the modal or backend touches them.
   */
  toggleReminder(key: ReminderKey, flagged: boolean): void {
    this.reminders.update(current => copyRemindersWith(current, key, flagged));
  }

  // ───────────────────────────── Phase 3 input handlers ─────────────────────────────

  onPhase3NotesInput(event: Event): void {
    const value = (event.target as HTMLTextAreaElement).value;
    if (value.length <= this.maxNotesLength) this.phase3Notes.set(value);
  }

  togglePhase3StatusRadio(target: PermitPhaseCompletionStatus): void {
    this.phase3Status.update(current => current === target ? undefined : target);
  }

  togglePhase3NextActionDatePicker(): void {
    this.showPhase3NextActionDatePicker.update(v => !v);
  }

  handlePhase3NextActionDateSelected(date: Date | null): void {
    this.phase3NextActionDate.set(date ? toIsoDate(date) : undefined);
    this.showPhase3NextActionDatePicker.set(false);
  }

  toggleExpectedEngineerReturnDatePicker(): void {
    this.showExpectedEngineerReturnDatePicker.update(v => !v);
  }

  handleExpectedEngineerReturnDateSelected(date: Date | null): void {
    this.expectedEngineerReturnDate.set(date ? toIsoDate(date) : undefined);
    this.showExpectedEngineerReturnDatePicker.set(false);
  }

  togglePreConstructionMeetingDatePicker(): void {
    this.showPreConstructionMeetingDatePicker.update(v => !v);
  }

  handlePreConstructionMeetingDateSelected(date: Date | null): void {
    this.preConstructionMeetingDate.set(date ? toIsoDate(date) : undefined);
    this.showPreConstructionMeetingDatePicker.set(false);
  }

  // ───────────────────────────── Phase 4 input handlers ─────────────────────────────

  onPhase4NotesInput(event: Event): void {
    const value = (event.target as HTMLTextAreaElement).value;
    if (value.length <= this.maxNotesLength) this.phase4Notes.set(value);
  }

  togglePhase4StatusRadio(target: PermitPhaseCompletionStatus): void {
    this.phase4Status.update(current => current === target ? undefined : target);
  }

  togglePhase4NextActionDatePicker(): void { this.showPhase4NextActionDatePicker.update(v => !v); }
  handlePhase4NextActionDateSelected(date: Date | null): void {
    this.phase4NextActionDate.set(date ? toIsoDate(date) : undefined);
    this.showPhase4NextActionDatePicker.set(false);
  }

  togglePermitAppSentDatePicker(): void { this.showPermitAppSentDatePicker.update(v => !v); }
  handlePermitAppSentDateSelected(date: Date | null): void {
    this.permitAppSentDate.set(date ? toIsoDate(date) : undefined);
    this.showPermitAppSentDatePicker.set(false);
  }

  toggleBuildingExpectedReturnDatePicker(): void { this.showBuildingExpectedReturnDatePicker.update(v => !v); }
  handleBuildingExpectedReturnDateSelected(date: Date | null): void {
    this.buildingExpectedReturnDate.set(date ? toIsoDate(date) : undefined);
    this.showBuildingExpectedReturnDatePicker.set(false);
  }

  togglePermitExpirationDatePicker(): void { this.showPermitExpirationDatePicker.update(v => !v); }
  handlePermitExpirationDateSelected(date: Date | null): void {
    this.permitExpirationDate.set(date ? toIsoDate(date) : undefined);
    this.showPermitExpirationDatePicker.set(false);
  }

  onCostOfPermitsBlur(): void {
    const parsed = parseMoneyInput(this.costOfPermitsDisplay());
    this.costOfPermits.set(parsed);
    this.costOfPermitsDisplay.set(formatMoneyForDisplay(parsed));
  }

  onCostOfThirdPartyDocsServicesBlur(): void {
    const parsed = parseMoneyInput(this.costOfThirdPartyDocsServicesDisplay());
    this.costOfThirdPartyDocsServices.set(parsed);
    this.costOfThirdPartyDocsServicesDisplay.set(formatMoneyForDisplay(parsed));
  }

  // ───────────────────────────── Phase 5 input handlers ─────────────────────────────

  onPhase5NotesInput(event: Event): void {
    const value = (event.target as HTMLTextAreaElement).value;
    if (value.length <= this.maxNotesLength) this.phase5Notes.set(value);
  }

  togglePhase5StatusRadio(target: PermitPhaseCompletionStatus): void {
    this.phase5Status.update(current => current === target ? undefined : target);
  }

  togglePhase5NextActionDatePicker(): void { this.showPhase5NextActionDatePicker.update(v => !v); }
  handlePhase5NextActionDateSelected(date: Date | null): void {
    this.phase5NextActionDate.set(date ? toIsoDate(date) : undefined);
    this.showPhase5NextActionDatePicker.set(false);
  }

  toggleMidInspectionDatePicker(): void { this.showMidInspectionDatePicker.update(v => !v); }
  handleMidInspectionDateSelected(date: Date | null): void {
    this.midInspectionDate.set(date ? toIsoDate(date) : undefined);
    this.showMidInspectionDatePicker.set(false);
  }

  toggleFinalInspectionDatePicker(): void { this.showFinalInspectionDatePicker.update(v => !v); }
  handleFinalInspectionDateSelected(date: Date | null): void {
    this.finalInspectionDate.set(date ? toIsoDate(date) : undefined);
    this.showFinalInspectionDatePicker.set(false);
  }

  /**
   * Loaded once when the modal opens — populates the Engineer of Record dropdown
   * with every eligible engineer across all branches. Engineers travel between
   * branches, so a permit at any location may be assigned to any of them.
   */
  loadEngineersOfRecord(): void {
    this.engineerOfRecordLoadError.set(null);
    this.apiClient.getEngineersOfRecord()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: users => this.engineerOfRecordUsers.set(users),
        error: err => {
          console.error('Failed to load engineers of record', err);
          this.engineerOfRecordLoadError.set('Unable to load engineers. Try refreshing.');
        },
      });
  }

  formatDateForDisplay(iso: string | undefined): string {
    if (!iso) return '';
    const [year, month, day] = iso.split('-');
    if (!year || !month || !day) return iso;
    return `${month}/${day}/${year}`;
  }

  save(): void {
    if (this.isSaving() || this.isDeleting()) return;
    this.isSaving.set(true);
    this.saveError.set(undefined);

    const existing = this.permit();
    // Unified save: one request commits every engaged-phase patch in a single transaction.
    // The backend's UpdatePermitHandler fans out per-phase touchpoint publishes in phase
    // 1→5 order, and the aggregate lazily materializes PermitPhaseStatus rows for any
    // visited phase whose right-rail patch is present.
    const request$ = existing
      ? this.apiClient.savePermit(this.projectId(), this.permitId(), this.buildUpdateRequest(existing))
      : this.apiClient.createPermit(this.projectId(), this.buildCreateRequest());

    request$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          const action: PermitEditResult['action'] = existing ? 'updated' : 'created';
          const id = existing ? this.permitId() : response.id;
          this.activeModal.close({ action, id } as PermitEditResult);
        },
        error: () => {
          this.isSaving.set(false);
          this.saveError.set('Failed to save permit. Please try again.');
        },
      });
  }

  delete(): void {
    if (!this.permit() || this.isDeleting() || this.isSaving()) return;

    const modalRef = this.modalService.openConfirmModal(
      'Delete Permit',
      'Are you sure you want to delete this permit? This action cannot be undone.',
      'Delete',
      'alert-danger',
    );

    modalRef.result
      .then((confirmed: boolean) => {
        if (confirmed) this.performDelete();
      })
      .catch(() => {
        // dismissed — no-op
      });
  }

  private performDelete(): void {
    this.isDeleting.set(true);
    this.saveError.set(undefined);

    this.apiClient.deletePermit(this.projectId(), this.permitId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.activeModal.close({ action: 'deleted', id: this.permitId() } as PermitEditResult);
        },
        error: () => {
          this.isDeleting.set(false);
          this.saveError.set('Failed to delete permit. Please try again.');
        },
      });
  }

  cancel(): void {
    this.activeModal.dismiss();
  }

  private populateFromResponse(response: PermitInfo): void {
    // Phase 1 — header
    this.permitNumber.set(response.permitNumber ?? '');
    this.permitName.set(response.permitName ?? '');
    this.countyTownship.set(response.countyTownship ?? '');
    this.permitTypeIds.set([...(response.permitTypeIds ?? [])]);

    // Phase 1 — right rail
    const phase1 = (response.phaseStatuses ?? []).find(s => asPhase(s.phase) === PermitPhase.ProgramDetails);
    this.notes.set(phase1?.notes ?? '');
    this.nextActionDate.set(phase1?.nextActionDate ? formatUtcIsoDate(phase1.nextActionDate) : undefined);
    const phase1Status = asStatus(phase1?.status);
    this.phaseStatus.set(
      phase1Status === PermitPhaseCompletionStatus.Completed || phase1Status === PermitPhaseCompletionStatus.NotNeeded
        ? phase1Status
        : undefined,
    );

    // Reminders snapshot — keep the load value so the save flow can diff against it.
    this.loadedReminders = response.reminders ?? new PermitReminders();
    this.reminders.set(response.reminders ?? new PermitReminders());

    // Phase 2 — Preparation header. Pull straight from the PermitInfo (top-level fields,
    // not the phase row).
    this.engineeringIntakeProvidedByCfi.set(asEngineeringIntakeProvidedByCfi(response.engineeringIntakeProvidedByCfi));
    this.cfiRemainingNeeds.set(response.cfiRemainingNeeds ?? '');
    this.assignedAdminCad.set(response.assignedAdminCad ?? '');

    const soilReportStatus = asPreparationItemStatus(response.soilReport);
    this.soilReport.set(soilReportStatus);
    this.soilReportExpectedReturnDate.set(
      response.soilReportExpectedReturnDate ? formatUtcIsoDate(response.soilReportExpectedReturnDate) : undefined,
    );

    const platSurveyStatus = asPreparationItemStatus(response.platSurveyIlc);
    this.platSurveyIlc.set(platSurveyStatus);

    const neighborStatus = asPreparationItemStatus(response.neighborNotification);
    this.neighborNotification.set(neighborStatus);

    const testPitStatus = asPreparationItemStatus(response.testPitTestPier);
    this.testPitTestPier.set(testPitStatus);
    this.testPitDate.set(response.testPitDate ? formatUtcIsoDate(response.testPitDate) : undefined);

    const electricalStatus = asSubcontractedPermitStatus(response.electricalPermitNeeded);
    this.electricalPermitNeeded.set(electricalStatus);
    this.electricalPermitNumber.set(response.electricalPermitNumber ?? '');

    const plumbingStatus = asSubcontractedPermitStatus(response.plumbingPermitNeeded);
    this.plumbingPermitNeeded.set(plumbingStatus);
    this.plumbingPermitNumber.set(response.plumbingPermitNumber ?? '');

    // Phase 2 — right rail
    const phase2 = (response.phaseStatuses ?? []).find(s => asPhase(s.phase) === PermitPhase.Preparation);
    this.phase2Notes.set(phase2?.notes ?? '');
    this.phase2NextActionDate.set(phase2?.nextActionDate ? formatUtcIsoDate(phase2.nextActionDate) : undefined);
    const phase2Status = asStatus(phase2?.status);
    this.phase2Status.set(
      phase2Status === PermitPhaseCompletionStatus.Completed || phase2Status === PermitPhaseCompletionStatus.NotNeeded
        ? phase2Status
        : undefined,
    );

    // Phase 3 — Engineering header (EoR + the phase 3-specific fields). Shared-with-Phase-2
    // fields (soilReport, platSurveyIlc, testPitTestPier) were already populated above.
    this.engineerOfRecordUserId.set(response.engineerOfRecordUserId);

    this.expectedEngineerReturnDate.set(
      response.expectedEngineerReturnDate ? formatUtcIsoDate(response.expectedEngineerReturnDate) : undefined,
    );

    const docStatus = asEngineeringDocStatus(response.engineeringDocStatus);
    this.engineeringDocStatus.set(docStatus);

    const thirdPartyStatus = asThirdPartyInspectionsStatus(response.thirdPartyInspections);
    this.thirdPartyInspections.set(thirdPartyStatus);
    this.thirdPartyInspector.set(response.thirdPartyInspector ?? '');

    const signedStmt = asSignedStatementStatus(response.signedStatementOfSpecialInspection);
    this.signedStatementOfSpecialInspection.set(signedStmt);

    const preCon = asPreConstructionMeetingStatus(response.preConstructionMeeting);
    this.preConstructionMeeting.set(preCon);
    this.preConstructionMeetingDate.set(
      response.preConstructionMeetingDate ? formatUtcIsoDate(response.preConstructionMeetingDate) : undefined,
    );

    // Phase 3 — right rail
    const phase3 = (response.phaseStatuses ?? []).find(s => asPhase(s.phase) === PermitPhase.Engineering);
    this.phase3Notes.set(phase3?.notes ?? '');
    this.phase3NextActionDate.set(phase3?.nextActionDate ? formatUtcIsoDate(phase3.nextActionDate) : undefined);
    const phase3Status = asStatus(phase3?.status);
    this.phase3Status.set(
      phase3Status === PermitPhaseCompletionStatus.Completed || phase3Status === PermitPhaseCompletionStatus.NotNeeded
        ? phase3Status
        : undefined,
    );

    // Phase 4 — Building header
    this.permitAppSentDate.set(
      response.permitAppSentDate ? formatUtcIsoDate(response.permitAppSentDate) : undefined,
    );
    this.buildingExpectedReturnDate.set(
      response.buildingExpectedReturnDate ? formatUtcIsoDate(response.buildingExpectedReturnDate) : undefined,
    );
    this.buildingPermitStatus.set(asPermitStatus(response.permitStatus));
    this.costOfPermits.set(response.costOfPermits);
    this.costOfPermitsDisplay.set(formatMoneyForDisplay(response.costOfPermits));
    this.costOfThirdPartyDocsServices.set(response.costOfThirdPartyDocsServices);
    this.costOfThirdPartyDocsServicesDisplay.set(formatMoneyForDisplay(response.costOfThirdPartyDocsServices));
    this.permitExpirationDate.set(
      response.permitExpirationDate ? formatUtcIsoDate(response.permitExpirationDate) : undefined,
    );

    // Phase 4 — right rail
    const phase4 = (response.phaseStatuses ?? []).find(s => asPhase(s.phase) === PermitPhase.Building);
    this.phase4Notes.set(phase4?.notes ?? '');
    this.phase4NextActionDate.set(phase4?.nextActionDate ? formatUtcIsoDate(phase4.nextActionDate) : undefined);
    const phase4Status = asStatus(phase4?.status);
    this.phase4Status.set(
      phase4Status === PermitPhaseCompletionStatus.Completed || phase4Status === PermitPhaseCompletionStatus.NotNeeded
        ? phase4Status
        : undefined,
    );

    // Phase 5 — Open Permit header
    const midType = asMidInspectionType(response.midInspectionType);
    this.midInspectionType.set(midType);
    this.midInspectionDate.set(response.midInspectionDate ? formatUtcIsoDate(response.midInspectionDate) : undefined);
    this.midInspectionInspector.set(response.midInspectionInspector ?? '');
    this.midInspectionStatus.set(asMidInspectionStatus(response.midInspectionStatus));

    this.electricalPermitStatus.set(asExternalPermitStatus(response.electricalPermitStatus));
    this.plumbingPermitStatus.set(asExternalPermitStatus(response.plumbingPermitStatus));

    const asBuild = asPermitAsBuildRequiredStatus(response.permitAsBuildRequired);
    this.permitAsBuildRequired.set(asBuild);

    const finalType = asFinalInspectionType(response.finalInspectionType);
    this.finalInspectionType.set(finalType);
    this.finalInspectionDate.set(
      response.finalInspectionDate ? formatUtcIsoDate(response.finalInspectionDate) : undefined,
    );
    this.finalInspectionInspector.set(response.finalInspectionInspector ?? '');
    this.finalInspectionStatus.set(asFinalInspectionStatus(response.finalInspectionStatus));
    this.inspectionFailureReason.set(response.inspectionFailureReason ?? '');

    // Phase 5 — right rail
    const phase5 = (response.phaseStatuses ?? []).find(s => asPhase(s.phase) === PermitPhase.OpenPermit);
    this.phase5Notes.set(phase5?.notes ?? '');
    this.phase5NextActionDate.set(phase5?.nextActionDate ? formatUtcIsoDate(phase5.nextActionDate) : undefined);
    const phase5Status = asStatus(phase5?.status);
    this.phase5Status.set(
      phase5Status === PermitPhaseCompletionStatus.Completed || phase5Status === PermitPhaseCompletionStatus.NotNeeded
        ? phase5Status
        : undefined,
    );

    // Open to the earliest phase that isn't yet Completed or NotNeeded — mirrors the
    // project-page "Status" column so editing lands on the same phase the row advertised.
    // When every phase is resolved we fall back to the last phase (Open Permit) since
    // there's no "Complete" landing page in the stepper.
    const target = this.computeCurrentPhase(response);
    this.currentPhase.set(target);
    this.markVisited(target);
  }

  private computeCurrentPhase(response: PermitInfo): PermitPhase {
    const resolved = new Set<PermitPhase>();
    for (const row of response.phaseStatuses ?? []) {
      const phase = asPhase(row.phase);
      const status = asStatus(row.status);
      if (phase === undefined) continue;
      if (status === PermitPhaseCompletionStatus.Completed || status === PermitPhaseCompletionStatus.NotNeeded) {
        resolved.add(phase);
      }
    }
    const ordered: readonly PermitPhase[] = [
      PermitPhase.ProgramDetails,
      PermitPhase.Preparation,
      PermitPhase.Engineering,
      PermitPhase.Building,
      PermitPhase.OpenPermit,
    ];
    return ordered.find(p => !resolved.has(p)) ?? PermitPhase.OpenPermit;
  }

  // ============================================================
  //  Unified payload builders (one request commits every engaged phase)
  // ============================================================

  private buildCreateRequest(): CreatePermitRequest {
    const permitNumber = trim(this.permitNumber());
    const permitName = trim(this.permitName());
    const countyTownship = trim(this.countyTownship());

    // For create we don't have an "existing" baseline — every per-phase patch is built
    // against an empty PermitInfo so clear-flags default to false (nothing to clear).
    const blank = new PermitInfo({
      id: '',
      projectId: '',
      phaseStatuses: [],
      permitTypeIds: [],
      resolvablePhases: [],
      createdAt: new Date(),
    });

    // Mirror buildUpdateRequest: only send Phase 2-5 patches for phases the user
    // engaged via the stepper. Phase 1 is always sent (it's the landing tab and the
    // backend seeds the Phase 1 row on create regardless).
    const visited = this.visitedPhases();
    return new CreatePermitRequest({
      permitNumber: permitNumber || undefined,
      permitName: permitName || undefined,
      countyTownship: countyTownship || undefined,
      engineerOfRecordUserId: this.engineerOfRecordUserId(),
      permitTypeIds: [...this.permitTypeIds()],
      phase2: visited.has(PermitPhase.Preparation) ? this.buildPhase2Patch(blank) : undefined,
      phase3: visited.has(PermitPhase.Engineering) ? this.buildPhase3Patch(blank) : undefined,
      phase4: visited.has(PermitPhase.Building)    ? this.buildPhase4Patch(blank) : undefined,
      phase5: visited.has(PermitPhase.OpenPermit)  ? this.buildPhase5Patch(blank) : undefined,
      phase1RightRail: this.buildPhase1RightRail(blank),
      phase2RightRail: visited.has(PermitPhase.Preparation) ? this.buildPhase2RightRail(blank) : undefined,
      phase3RightRail: visited.has(PermitPhase.Engineering) ? this.buildPhase3RightRail(blank) : undefined,
      phase4RightRail: visited.has(PermitPhase.Building)    ? this.buildPhase4RightRail(blank) : undefined,
      phase5RightRail: visited.has(PermitPhase.OpenPermit)  ? this.buildPhase5RightRail(blank) : undefined,
      reminders: this.buildRemindersPatch(),
    });
  }

  private buildUpdateRequest(existing: PermitInfo): UpdatePermitRequest {
    const visited = this.visitedPhases();
    return new UpdatePermitRequest({
      header: this.buildHeaderPatch(existing),
      permitTypeIds: [...this.permitTypeIds()],
      phase2: visited.has(PermitPhase.Preparation) ? this.buildPhase2Patch(existing) : undefined,
      phase3: visited.has(PermitPhase.Engineering) ? this.buildPhase3Patch(existing) : undefined,
      phase4: visited.has(PermitPhase.Building)    ? this.buildPhase4Patch(existing) : undefined,
      phase5: visited.has(PermitPhase.OpenPermit)  ? this.buildPhase5Patch(existing) : undefined,
      phase1RightRail: this.buildPhase1RightRail(existing),
      phase2RightRail: visited.has(PermitPhase.Preparation) ? this.buildPhase2RightRail(existing) : undefined,
      phase3RightRail: visited.has(PermitPhase.Engineering) ? this.buildPhase3RightRail(existing) : undefined,
      phase4RightRail: visited.has(PermitPhase.Building)    ? this.buildPhase4RightRail(existing) : undefined,
      phase5RightRail: visited.has(PermitPhase.OpenPermit)  ? this.buildPhase5RightRail(existing) : undefined,
      reminders: this.buildRemindersPatch(),
    });
  }

  private buildRemindersPatch(): PermitRemindersPatch | undefined {
    return diffRemindersPatch(this.loadedReminders, this.reminders());
  }

  private buildHeaderPatch(existing: PermitInfo): PermitHeaderPatch {
    const permitNumber = trim(this.permitNumber());
    const permitName = trim(this.permitName());
    const countyTownship = trim(this.countyTownship());
    return new PermitHeaderPatch({
      permitNumber: permitNumber || undefined,
      clearPermitNumber: !permitNumber && !!existing.permitNumber,
      permitName: permitName || undefined,
      clearPermitName: !permitName && !!existing.permitName,
      countyTownship: countyTownship || undefined,
      clearCountyTownship: !countyTownship && !!existing.countyTownship,
      // EngineerOfRecord lives on the header (Phase 3 UI binds the same underlying field).
      // Kind is derived server-side from UserId; we only send UserId + Clear flag.
      engineerOfRecordUserId: this.engineerOfRecordUserId(),
      clearEngineerOfRecord: !this.engineerOfRecordUserId() && !!existing.engineerOfRecordUserId,
    });
  }

  // Compare the user's current radio selection (Completed/NotNeeded/undefined) against
  // the persisted row, normalizing both to InProgress when no terminal status is set.
  // Returns the value to send for `status`: the new normalized status if it changed,
  // or undefined ("leave alone") if it didn't — including the case where the user
  // visited the tab but never touched the radio.
  private diffStatus(
    currentSignal: PermitPhaseCompletionStatus | undefined,
    persistedStatus: PermitPhaseCompletionStatus | string | undefined | null,
  ): PermitPhaseCompletionStatus | undefined {
    const currentNormalized = currentSignal ?? PermitPhaseCompletionStatus.InProgress;
    const persistedNormalized = asStatus(persistedStatus) ?? PermitPhaseCompletionStatus.InProgress;
    return currentNormalized === persistedNormalized ? undefined : currentNormalized;
  }

  private buildPhase1RightRail(existing: PermitInfo): PermitPhaseStatusPatch {
    const existingPhase = (existing.phaseStatuses ?? []).find(s => asPhase(s.phase) === PermitPhase.ProgramDetails);
    const notes = trim(this.notes());
    return new PermitPhaseStatusPatch({
      status: this.diffStatus(this.phaseStatus(), existingPhase?.status),
      nextActionDate: this.nextActionDate() ? new Date(this.nextActionDate()! + 'T00:00:00') : undefined,
      clearNextActionDate: !this.nextActionDate() && !!existingPhase?.nextActionDate,
      notes: notes || undefined,
      clearNotes: !notes && !!existingPhase?.notes,
    });
  }

  private buildPhase2RightRail(existing: PermitInfo): PermitPhaseStatusPatch {
    const phase2 = (existing.phaseStatuses ?? []).find(s => asPhase(s.phase) === PermitPhase.Preparation);
    const notes = trim(this.phase2Notes());
    return new PermitPhaseStatusPatch({
      status: this.diffStatus(this.phase2Status(), phase2?.status),
      nextActionDate: this.phase2NextActionDate() ? new Date(this.phase2NextActionDate()! + 'T00:00:00') : undefined,
      clearNextActionDate: !this.phase2NextActionDate() && !!phase2?.nextActionDate,
      notes: notes || undefined,
      clearNotes: !notes && !!phase2?.notes,
    });
  }

  private buildPhase3RightRail(existing: PermitInfo): PermitPhaseStatusPatch {
    const phase3 = (existing.phaseStatuses ?? []).find(s => asPhase(s.phase) === PermitPhase.Engineering);
    const notes = trim(this.phase3Notes());
    return new PermitPhaseStatusPatch({
      status: this.diffStatus(this.phase3Status(), phase3?.status),
      nextActionDate: this.phase3NextActionDate() ? new Date(this.phase3NextActionDate()! + 'T00:00:00') : undefined,
      clearNextActionDate: !this.phase3NextActionDate() && !!phase3?.nextActionDate,
      notes: notes || undefined,
      clearNotes: !notes && !!phase3?.notes,
    });
  }

  private buildPhase4RightRail(existing: PermitInfo): PermitPhaseStatusPatch {
    const phase4 = (existing.phaseStatuses ?? []).find(s => asPhase(s.phase) === PermitPhase.Building);
    const notes = trim(this.phase4Notes());
    return new PermitPhaseStatusPatch({
      status: this.diffStatus(this.phase4Status(), phase4?.status),
      nextActionDate: this.phase4NextActionDate() ? new Date(this.phase4NextActionDate()! + 'T00:00:00') : undefined,
      clearNextActionDate: !this.phase4NextActionDate() && !!phase4?.nextActionDate,
      notes: notes || undefined,
      clearNotes: !notes && !!phase4?.notes,
    });
  }

  private buildPhase5RightRail(existing: PermitInfo): PermitPhaseStatusPatch {
    const phase5 = (existing.phaseStatuses ?? []).find(s => asPhase(s.phase) === PermitPhase.OpenPermit);
    const notes = trim(this.phase5Notes());
    return new PermitPhaseStatusPatch({
      status: this.diffStatus(this.phase5Status(), phase5?.status),
      nextActionDate: this.phase5NextActionDate() ? new Date(this.phase5NextActionDate()! + 'T00:00:00') : undefined,
      clearNextActionDate: !this.phase5NextActionDate() && !!phase5?.nextActionDate,
      notes: notes || undefined,
      clearNotes: !notes && !!phase5?.notes,
    });
  }

  private buildPhase5Patch(existing: PermitInfo): PermitPhase5Patch {
    const midInspectionInspector = trim(this.midInspectionInspector());
    const finalInspectionInspector = trim(this.finalInspectionInspector());
    const inspectionFailureReason = trim(this.inspectionFailureReason());

    return new PermitPhase5Patch({
      midInspectionType: this.midInspectionType(),
      clearMidInspectionType: isCleared(this.midInspectionType()) && existing.midInspectionType !== undefined,
      midInspectionDate: this.midInspectionDate() ? new Date(this.midInspectionDate()! + 'T00:00:00') : undefined,
      clearMidInspectionDate: !this.midInspectionDate() && !!existing.midInspectionDate,
      midInspectionInspector: midInspectionInspector || undefined,
      clearMidInspectionInspector: !midInspectionInspector && !!existing.midInspectionInspector,
      midInspectionStatus: this.midInspectionStatus(),
      clearMidInspectionStatus: isCleared(this.midInspectionStatus()) && existing.midInspectionStatus !== undefined,

      // Electrical / Plumbing Permit Status — plain dropdowns (N/A is a real enum value)
      electricalPermitStatus: this.electricalPermitStatus(),
      clearElectricalPermitStatus: isCleared(this.electricalPermitStatus()) && existing.electricalPermitStatus !== undefined,
      plumbingPermitStatus: this.plumbingPermitStatus(),
      clearPlumbingPermitStatus: isCleared(this.plumbingPermitStatus()) && existing.plumbingPermitStatus !== undefined,

      permitAsBuildRequired: this.permitAsBuildRequired(),
      clearPermitAsBuildRequired: isCleared(this.permitAsBuildRequired()) && existing.permitAsBuildRequired !== undefined,

      finalInspectionType: this.finalInspectionType(),
      clearFinalInspectionType: isCleared(this.finalInspectionType()) && existing.finalInspectionType !== undefined,
      finalInspectionDate: this.finalInspectionDate() ? new Date(this.finalInspectionDate()! + 'T00:00:00') : undefined,
      clearFinalInspectionDate: !this.finalInspectionDate() && !!existing.finalInspectionDate,
      finalInspectionInspector: finalInspectionInspector || undefined,
      clearFinalInspectionInspector: !finalInspectionInspector && !!existing.finalInspectionInspector,
      finalInspectionStatus: this.finalInspectionStatus(),
      clearFinalInspectionStatus: isCleared(this.finalInspectionStatus()) && existing.finalInspectionStatus !== undefined,
      inspectionFailureReason: inspectionFailureReason || undefined,
      clearInspectionFailureReason: !inspectionFailureReason && !!existing.inspectionFailureReason,
    });
  }

  private buildPhase4Patch(existing: PermitInfo): PermitPhase4Patch {
    const costOfPermits = this.costOfPermits();
    const costOfThirdParty = this.costOfThirdPartyDocsServices();

    return new PermitPhase4Patch({
      permitAppSentDate: this.permitAppSentDate() ? new Date(this.permitAppSentDate()! + 'T00:00:00') : undefined,
      clearPermitAppSentDate: !this.permitAppSentDate() && !!existing.permitAppSentDate,
      buildingExpectedReturnDate: this.buildingExpectedReturnDate()
        ? new Date(this.buildingExpectedReturnDate()! + 'T00:00:00') : undefined,
      clearBuildingExpectedReturnDate: !this.buildingExpectedReturnDate() && !!existing.buildingExpectedReturnDate,
      permitStatus: this.buildingPermitStatus(),
      clearPermitStatus: isCleared(this.buildingPermitStatus()) && existing.permitStatus !== undefined,
      costOfPermits: costOfPermits !== undefined ? costOfPermits : undefined,
      clearCostOfPermits: isCleared(costOfPermits) && existing.costOfPermits !== undefined,
      costOfThirdPartyDocsServices: costOfThirdParty !== undefined ? costOfThirdParty : undefined,
      clearCostOfThirdPartyDocsServices: isCleared(costOfThirdParty) && existing.costOfThirdPartyDocsServices !== undefined,
      permitExpirationDate: this.permitExpirationDate()
        ? new Date(this.permitExpirationDate()! + 'T00:00:00') : undefined,
      clearPermitExpirationDate: !this.permitExpirationDate() && !!existing.permitExpirationDate,
    });
  }

  private buildPhase3Patch(existing: PermitInfo): PermitPhase3Patch {
    const thirdPartyInspector = trim(this.thirdPartyInspector());

    return new PermitPhase3Patch({
      expectedEngineerReturnDate: this.expectedEngineerReturnDate()
        ? new Date(this.expectedEngineerReturnDate()! + 'T00:00:00') : undefined,
      clearExpectedEngineerReturnDate: !this.expectedEngineerReturnDate() && !!existing.expectedEngineerReturnDate,

      engineeringDocStatus: this.engineeringDocStatus(),
      clearEngineeringDocStatus: isCleared(this.engineeringDocStatus()) && existing.engineeringDocStatus !== undefined,

      // Shared with Phase 2 — same signals, single DB column.
      soilReport: this.soilReport(),
      clearSoilReport: isCleared(this.soilReport()) && existing.soilReport !== undefined,
      platSurveyIlc: this.platSurveyIlc(),
      clearPlatSurveyIlc: isCleared(this.platSurveyIlc()) && existing.platSurveyIlc !== undefined,
      testPitTestPier: this.testPitTestPier(),
      clearTestPitTestPier: isCleared(this.testPitTestPier()) && existing.testPitTestPier !== undefined,

      thirdPartyInspections: this.thirdPartyInspections(),
      clearThirdPartyInspections: isCleared(this.thirdPartyInspections()) && existing.thirdPartyInspections !== undefined,
      thirdPartyInspector: thirdPartyInspector || undefined,
      clearThirdPartyInspector: !thirdPartyInspector && !!existing.thirdPartyInspector,

      signedStatementOfSpecialInspection: this.signedStatementOfSpecialInspection(),
      clearSignedStatementOfSpecialInspection:
        isCleared(this.signedStatementOfSpecialInspection()) && existing.signedStatementOfSpecialInspection !== undefined,

      preConstructionMeeting: this.preConstructionMeeting(),
      clearPreConstructionMeeting: isCleared(this.preConstructionMeeting()) && existing.preConstructionMeeting !== undefined,
      preConstructionMeetingDate: this.preConstructionMeetingDate()
        ? new Date(this.preConstructionMeetingDate()! + 'T00:00:00') : undefined,
      clearPreConstructionMeetingDate: !this.preConstructionMeetingDate() && !!existing.preConstructionMeetingDate,
    });
  }

  private buildPhase2Patch(existing: PermitInfo): PermitPhase2Patch {
    const eIntake = this.engineeringIntakeProvidedByCfi();
    const cfiRemainingNeeds = trim(this.cfiRemainingNeeds());
    const assignedAdminCad = trim(this.assignedAdminCad());
    const electricalPermitNumber = trim(this.electricalPermitNumber());
    const plumbingPermitNumber = trim(this.plumbingPermitNumber());

    return new PermitPhase2Patch({
      engineeringIntakeProvidedByCfi: eIntake,
      clearEngineeringIntakeProvidedByCfi: isCleared(eIntake) && existing.engineeringIntakeProvidedByCfi !== undefined,
      cfiRemainingNeeds: cfiRemainingNeeds || undefined,
      clearCfiRemainingNeeds: !cfiRemainingNeeds && !!existing.cfiRemainingNeeds,
      assignedAdminCad: assignedAdminCad || undefined,
      clearAssignedAdminCad: !assignedAdminCad && !!existing.assignedAdminCad,

      soilReport: this.soilReport(),
      clearSoilReport: isCleared(this.soilReport()) && existing.soilReport !== undefined,
      soilReportExpectedReturnDate: this.soilReportExpectedReturnDate()
        ? new Date(this.soilReportExpectedReturnDate()! + 'T00:00:00') : undefined,
      clearSoilReportExpectedReturnDate: !this.soilReportExpectedReturnDate() && !!existing.soilReportExpectedReturnDate,

      platSurveyIlc: this.platSurveyIlc(),
      clearPlatSurveyIlc: isCleared(this.platSurveyIlc()) && existing.platSurveyIlc !== undefined,

      neighborNotification: this.neighborNotification(),
      clearNeighborNotification: isCleared(this.neighborNotification()) && existing.neighborNotification !== undefined,

      testPitTestPier: this.testPitTestPier(),
      clearTestPitTestPier: isCleared(this.testPitTestPier()) && existing.testPitTestPier !== undefined,
      testPitDate: this.testPitDate() ? new Date(this.testPitDate()! + 'T00:00:00') : undefined,
      clearTestPitDate: !this.testPitDate() && !!existing.testPitDate,

      electricalPermitNeeded: this.electricalPermitNeeded(),
      clearElectricalPermitNeeded: isCleared(this.electricalPermitNeeded()) && existing.electricalPermitNeeded !== undefined,
      electricalPermitNumber: electricalPermitNumber || undefined,
      clearElectricalPermitNumber: !electricalPermitNumber && !!existing.electricalPermitNumber,

      plumbingPermitNeeded: this.plumbingPermitNeeded(),
      clearPlumbingPermitNeeded: isCleared(this.plumbingPermitNeeded()) && existing.plumbingPermitNeeded !== undefined,
      plumbingPermitNumber: plumbingPermitNumber || undefined,
      clearPlumbingPermitNumber: !plumbingPermitNumber && !!existing.plumbingPermitNumber,
    });
  }
}

function trim(value: string): string {
  return value.trim();
}

// ng-select emits `null` (not `undefined`) when the user clicks the 'x' clear control,
// so dropdown-backed signals can hold either. Use this for clear-flag computations.
function isCleared(value: unknown): boolean {
  return value === null || value === undefined;
}

// Format a numeric monetary value for the display-side of a $-prefixed text input.
// Empty string when undefined so the input renders blank; two decimals otherwise.
function formatMoneyForDisplay(value: number | undefined): string {
  return value == null ? '' : value.toFixed(2);
}

// Parse a $-prefixed text input back to a numeric monetary value. Empty/invalid
// resolves to undefined so the field round-trips through the clear flag. Mirrors
// the post-payment-modal blur handler.
function parseMoneyInput(raw: string): number | undefined {
  const trimmed = raw.trim();
  if (trimmed === '') return undefined;
  const num = parseFloat(trimmed.replace(/,/g, ''));
  return Number.isFinite(num) ? roundMoney(num) : undefined;
}

// Normalize wire values to the SDK enum member at the API boundary. Works for both the
// legacy numeric-valued SDK enums and the current string-valued enums (after Swashbuckle
// schema filter); unknown values resolve to undefined.
function asPhase(value: PermitPhase | string | undefined | null): PermitPhase | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'number') return value;
  const lookup = (PermitPhase as unknown as Record<string, PermitPhase>)[value];
  return lookup;
}

function asStatus(value: PermitPhaseCompletionStatus | string | undefined | null): PermitPhaseCompletionStatus | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'number') return value;
  const lookup = (PermitPhaseCompletionStatus as unknown as Record<string, PermitPhaseCompletionStatus>)[value];
  return lookup;
}

function asEngineeringIntakeProvidedByCfi(value: EngineeringIntakeProvidedByCfi | string | undefined | null): EngineeringIntakeProvidedByCfi | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'number') return value;
  const lookup = (EngineeringIntakeProvidedByCfi as unknown as Record<string, EngineeringIntakeProvidedByCfi>)[value];
  return lookup;
}

function asPreparationItemStatus(value: PreparationItemStatus | string | undefined | null): PreparationItemStatus | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'number') return value;
  const lookup = (PreparationItemStatus as unknown as Record<string, PreparationItemStatus>)[value];
  return lookup;
}

function asSubcontractedPermitStatus(value: SubcontractedPermitStatus | string | undefined | null): SubcontractedPermitStatus | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'number') return value;
  const lookup = (SubcontractedPermitStatus as unknown as Record<string, SubcontractedPermitStatus>)[value];
  return lookup;
}

function asEngineeringDocStatus(value: EngineeringDocStatus | string | undefined | null): EngineeringDocStatus | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'number') return value;
  const lookup = (EngineeringDocStatus as unknown as Record<string, EngineeringDocStatus>)[value];
  return lookup;
}

function asThirdPartyInspectionsStatus(value: ThirdPartyInspectionsStatus | string | undefined | null): ThirdPartyInspectionsStatus | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'number') return value;
  const lookup = (ThirdPartyInspectionsStatus as unknown as Record<string, ThirdPartyInspectionsStatus>)[value];
  return lookup;
}

function asSignedStatementStatus(value: SignedStatementStatus | string | undefined | null): SignedStatementStatus | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'number') return value;
  const lookup = (SignedStatementStatus as unknown as Record<string, SignedStatementStatus>)[value];
  return lookup;
}

function asPreConstructionMeetingStatus(value: PreConstructionMeetingStatus | string | undefined | null): PreConstructionMeetingStatus | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'number') return value;
  const lookup = (PreConstructionMeetingStatus as unknown as Record<string, PreConstructionMeetingStatus>)[value];
  return lookup;
}

function asPermitStatus(value: PermitStatus | string | undefined | null): PermitStatus | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'number') return value;
  const lookup = (PermitStatus as unknown as Record<string, PermitStatus>)[value];
  return lookup;
}

function asMidInspectionType(value: MidInspectionType | string | undefined | null): MidInspectionType | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'number') return value;
  const lookup = (MidInspectionType as unknown as Record<string, MidInspectionType>)[value];
  return lookup;
}

function asMidInspectionStatus(value: MidInspectionStatus | string | undefined | null): MidInspectionStatus | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'number') return value;
  const lookup = (MidInspectionStatus as unknown as Record<string, MidInspectionStatus>)[value];
  return lookup;
}

function asExternalPermitStatus(value: ExternalPermitStatus | string | undefined | null): ExternalPermitStatus | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'number') return value;
  const lookup = (ExternalPermitStatus as unknown as Record<string, ExternalPermitStatus>)[value];
  return lookup;
}

function asPermitAsBuildRequiredStatus(value: PermitAsBuildRequiredStatus | string | undefined | null): PermitAsBuildRequiredStatus | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'number') return value;
  const lookup = (PermitAsBuildRequiredStatus as unknown as Record<string, PermitAsBuildRequiredStatus>)[value];
  return lookup;
}

function asFinalInspectionType(value: FinalInspectionType | string | undefined | null): FinalInspectionType | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'number') return value;
  const lookup = (FinalInspectionType as unknown as Record<string, FinalInspectionType>)[value];
  return lookup;
}

function asFinalInspectionStatus(value: FinalInspectionStatus | string | undefined | null): FinalInspectionStatus | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'number') return value;
  const lookup = (FinalInspectionStatus as unknown as Record<string, FinalInspectionStatus>)[value];
  return lookup;
}

function toIsoDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// ── Reminders helpers ─────────────────────────────────────────────────────────

export type ReminderKey =
  | 'soilReport'
  | 'platSurveyIlc'
  | 'neighborNotification'
  | 'testPitTestPier'
  | 'electricalPermitNeeded'
  | 'plumbingPermitNeeded'
  | 'engineerOfRecord'
  | 'engineeringDocStatus'
  | 'thirdPartyInspections'
  | 'signedStatementOfSpecialInspection'
  | 'preConstructionMeeting'
  | 'midInspectionType'
  | 'permitAsBuildRequired'
  | 'finalInspectionType';

const REMINDER_KEYS: readonly ReminderKey[] = [
  'soilReport', 'platSurveyIlc', 'neighborNotification', 'testPitTestPier',
  'electricalPermitNeeded', 'plumbingPermitNeeded',
  'engineerOfRecord', 'engineeringDocStatus', 'thirdPartyInspections',
  'signedStatementOfSpecialInspection', 'preConstructionMeeting',
  'midInspectionType', 'permitAsBuildRequired', 'finalInspectionType',
];

export function copyRemindersWith(source: PermitReminders, key: ReminderKey, flagged: boolean): PermitReminders {
  const next = new PermitReminders();
  for (const k of REMINDER_KEYS) next[k] = source[k];
  next[key] = flagged;
  return next;
}

/**
 * Returns the sparse patch that captures only the reminder flags whose values changed
 * between the load snapshot and the user's current selections. Returns undefined when
 * nothing changed so the wire payload stays empty for that section.
 */
export function diffRemindersPatch(
  loaded: PermitReminders,
  current: PermitReminders,
): PermitRemindersPatch | undefined {
  const patch = new PermitRemindersPatch();
  let dirty = false;
  for (const key of REMINDER_KEYS) {
    if (loaded[key] !== current[key]) {
      patch[key] = current[key];
      dirty = true;
    }
  }
  return dirty ? patch : undefined;
}
